import { assertNotNull } from '@/helpers/typing'
import {
  createDocsView,
  DocsView,
  DocSortOption,
  filteredDocsByDocType,
} from './doc'
import { ListDescriptionView } from './docs.list.description'
import { ArticlesView } from './doc.article'
import { BooksView } from './doc.book'
import { CollectionsView } from './docs.list.collection'
import { PodcastsView } from './docs.list.podcasts'
import {
  ListDescriptionViewPodcast,
  PodcastEpisodesView,
} from './docs.list.podcasts.episodes'
import type {
  ApiResultDocs,
  Doc,
  DocType,
  CollectionListItem,
  ListItem,
  ListItemType,
  CustomList,
  CustomListWithDocs,
  DocsUserDataResponse,
} from './interfaces'

import { backend } from '@/services/backend'
import { loadBookListFromNativeStorage } from '@/services/native.storage'
import { offline } from '@/services/offline'
import { capitalizeTagFirstLetter, truncateSearchTerm } from '@/helpers/text'
import { docTypeLabel } from '@/models/doc'
import { Sentry } from '@/services/sentry'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import {
  continueReading19038Variant,
  saveForLater20321Treatment,
} from '@/services/ab'

/**
 * A minimal interface for the DocsView-like data classes
 * that can be displayed with a ListView.
 */
export interface DocsListView {
  get docTypeName(): ListItemType | null
  get filteredDocs(): ListItem[]
  get dataLength(): number
  setShowLimit(limit: number): void
  isDocSortVisible(): boolean
  setUserData(docsUserData?: DocsUserDataResponse): void
  get hasSetUserData(): boolean
}

/**
 * Listing view.
 *
 * Wrapper around the DocsView that provides additional settings and
 * logic for the ListPage component.
 *
 * The ListView represents the list page with this structure:
 *
 *   [ page title                        ]
 *
 *   [ Description (ListDescriptionView) ]
 *
 *   [ Title             ] [ filter box  ]
 *
 *   [ The list of documents (DocsView)  ]
 *   [ renderd with ListCard component   ]
 *
 * We have separate classes for the list of documents (DocsView) and
 * the description (ListDescriptionView) that vary depending on
 * the document type.
 *
 * The ListView is used in the ListPage component. The component is unified
 * and can be used for all document types (books, articles, podcasts, etc).
 *
 * At the same time, we have a way to display list items (documents) in a
 * different way. This is done by the ListCard component that uses the
 * dynamic ":is" attribute to render the component based on the document
 * type (for example, we have BookCard for books and ArticleCard for articles).
 *
 * In the case we add a new document type, we need to add a new card component
 * for it and add the mapping in the ListCard component.
 *
 * In pages where we need a different card than the default card for the given
 * document type, we can pass the component name of this different card to
 * `options.customCardComponent`. This card component also has to be mapped
 * in ListCard.
 *
 * To add a new listing page, we need to add a new route to the routes.ts
 * file and update the `createListViewByRoute` function below to create
 * the ListView with the correct DocsView and ListDescriptionView.
 *
 * Routes for ListView look like this:
 *
 * ```
 * {
 *   path: '/app/books/category/:category',
 *   name: 'bookCategory',
 *   component: ListPage,
 *   meta: {
 *     keepAlive: true,
 *     componentName: 'ListPage',
 *   },
 * }
 * ```
 *
 * And the appropriate code in `createListViewByRoute` function:
 *
 * ```
 * if (routeName === 'bookCategory') {
 *   const tag = decodeURI(params.category as string)
 *   const title = capitalizeTagFirstLetter(tag)
 *   const docsView = await loadBooks(tag)
 *   return new ListView(title, docsView)
 * }
 * ```
 *
 * The description view is optional and used for the top area of the list page.
 * For example for podcast with the list of episodes we have a podcast
 * description at the top with the image and "listen to original" link and
 * then we have the list of episodes below.
 *
 * We also have description view for collections, in the code it looks like this:
 *
 * ```
 * if (routeName === 'bookCollection') {
 *   const urlSlug = params.collection as string
 *   const { docsView, collection } = await loadCollectionBooks(urlSlug)
 *   const descriptionView = new ListDescriptionView(
 *     collection,
 *     collection.doc_count,
 *   )
 *   return new ListView(descriptionView.title, docsView, descriptionView)
 * }
 * ```
 *
 * Above we load the collection and create the `DocsView` for it, then also
 * create the `ListDescriptionView` and pass it to the ListView that manages
 * the page UI.
 */
export class ListView {
  private _title: string
  private _filterTitle: string
  private _description?: string

  private _docs: DocsListView
  private _descriptionView?: ListDescriptionView
  private _customList?: CustomList

  private _viewAllLink?: string
  private _viewAllText?: string

  private _customCardComponent?: string

  private _isHeaderCollapsed: boolean = false

  public constructor(
    title: string,
    docs: DocsListView,
    options?: {
      descriptionView?: ListDescriptionView
      customList?: CustomList
      filterTitle?: string
      description?: string
      viewAllLink?: string
      viewAllText?: string
      customCardComponent?: string
    },
  ) {
    this._title = title
    this._docs = docs
    this._descriptionView = options?.descriptionView
    this._customList = options?.customList
    this._filterTitle = options?.filterTitle || this._title
    this._description = options?.description
    this._viewAllLink = options?.viewAllLink
    this._viewAllText = options?.viewAllText
    this._customCardComponent = options?.customCardComponent
  }

  public pageTitle(): string {
    return this._title
  }

  public filterTitle(): string {
    return this._filterTitle
  }

  public description(): string | undefined {
    return this._description
  }

  public get docTypeName(): ListItemType | null {
    return this.docs().docTypeName
  }

  public notFoundMessage(): string {
    return `No ${this.docTypeLabelPlural} found.`
  }

  public docs(): DocsListView {
    return assertNotNull(this._docs)
  }

  public get dataLength(): number {
    return this.docs().dataLength
  }

  public hasDocs(): boolean {
    if (this._docs && this._docs.dataLength > 0) {
      return true
    }
    return false
  }

  public get docTypeLabel(): string {
    if (!this.docTypeName) {
      return 'content'
    }
    return docTypeLabel[this.docTypeName].singular
  }

  public get docTypeLabelPlural(): string {
    if (!this.docTypeName) {
      return 'content'
    }
    return docTypeLabel[this.docTypeName].plural
  }

  public get customList(): CustomList | undefined {
    return this._customList
  }

  public get descriptionView(): ListDescriptionView | undefined {
    return this._descriptionView
  }

  public get viewAllLink(): string | undefined {
    return this._viewAllLink
  }

  public get viewAllText(): string | undefined {
    return this._viewAllText
  }

  public get customCardComponent(): string | undefined {
    return this._customCardComponent
  }

  /**
   * Get `this.docTypeName()` formatted to be used in CSS classes.
   */
  get cssClassModifier(): string {
    if (!this.docTypeName) {
      return 'docs'
    }
    return this.docTypeName.replace(/_/g, '-') + 's'
  }

  /**
   * Get header state for listing pages
   */
  get isHeaderCollapsed(): boolean {
    return this._isHeaderCollapsed
  }

  /**
   * Set header state from scroll position
   */
  set isHeaderCollapsed(value: boolean) {
    this._isHeaderCollapsed = value
  }

  setShowLimit(limit: number): void {
    this.docs().setShowLimit(limit)
  }

  async setUserData(
    DocsUserDataPromise: Promise<DocsUserDataResponse>,
  ): Promise<void> {
    try {
      const docsUserData = await DocsUserDataPromise
      this.docs().setUserData(docsUserData)
    } catch (error) {
      Sentry.captureException(error)
    }
  }
}

/**
 * Listing page route names for books.
 *
 * How to add a new listing page:
 * - Add the route name to this enum.
 * - Add Listing page configuration to the ``ListingPages`` const.
 * - Add mapping between route name and user-friendly title to the
 *   ``pageTypeToTitleMap`` object.
 */
export enum BookPageType {
  all = 'all',
  popular = 'popular',
  new = 'new',
  favorites = 'favorites',
  reading = 'reading',
  finished = 'finished',
  history = 'history',
  offline = 'offline',
  free = 'free',
  recommended = 'recommended',
  category = 'category',
  search = 'search',
  downloads = 'downloads',
}

export enum ArticlePageType {
  all = 'all',
  favorites = 'favorites',
  viewed = 'viewed',
}

export enum PodcastPageType {
  favorites = 'favorites',
  reading = 'reading',
  finished = 'finished',
}

const userBookPages = [
  BookPageType.favorites,
  BookPageType.offline,
  BookPageType.reading,
  BookPageType.finished,
  BookPageType.history,
  BookPageType.downloads,
]

/**
 * Load books from the backend and create DocsView.
 */
async function loadBooks(
  tag?: string,
  docsUserData?: DocsUserDataResponse,
): Promise<DocsView> {
  if (offline.isOffline) {
    await loadBookListFromNativeStorage()
  }

  const response = await backend.getBooksWithRecommendations()
  const view = await BooksView.createView(
    response,
    docsUserData,
    undefined, // Default defaultSort
    true, // Set showCtaBlock
  )
  if (tag) {
    await view.setTag(tag)
  }
  return view
}

type DocsViewAndCollection = {
  docsView: DocsView
  collection: CollectionListItem
}

type DocsViewAndCustomList = {
  docsView: DocsView
  list: CustomList
}

/**
 * Load books from the backend and create DocsView.
 */
async function loadCollectionBooks(
  collectionUrlSlug: string,
): Promise<DocsViewAndCollection> {
  if (offline.isOffline) {
    await loadBookListFromNativeStorage()
  }

  const response = await backend.getCollectionBooks(collectionUrlSlug)
  return {
    docsView: await BooksView.createView(
      response,
      undefined, // No docsUserData
      undefined, // Default defaultSort
      true, // Set showCtaBlock
    ),
    collection: response.collection,
  }
}

/**
 * Load books from the backend and create DocsView.
 */
async function loadCustomListDocs(
  listUrlSlug: string,
  docsUserDataPromise: Promise<DocsUserDataResponse>,
): Promise<DocsViewAndCustomList> {
  const [customLists, docsUserData] = await Promise.all([
    backend.getCustomListDocs(listUrlSlug),
    docsUserDataPromise,
  ])
  assertNotNull(customLists.data)
  return {
    docsView: await BooksView.createView(
      customLists,
      docsUserData,
      DocSortOption.none,
      true, // Set showCtaBlock
    ),
    list: customLists.list,
  }
}

/**
 * Create a list view for books based on the page type, get data from the home API.
 */
async function createUserBooksListView(
  pageType: BookPageType,
  docsUserDataPromise: Promise<DocsUserDataResponse>,
): Promise<ListView> {
  const [docs, docsUserData] = await Promise.all([
    backend.getUserBooksWithRecommendations(),
    docsUserDataPromise,
  ])

  const docsView = await BooksView.createView(
    docs,
    docsUserData,
    undefined, // Default DocSortOption
    true, // Set showCtaBlock
  )

  switch (pageType) {
    case BookPageType.favorites:
      docsView.enableActionsButton()
      await docsView.setIsFavorite(true)
      return new ListView('Favorites', docsView)

    case BookPageType.reading:
      docsView.enableContinueReading()
      docsView.enableActionsButton()
      await docsView.setList('reading')
      await docsView.sort(DocSortOption.accessed)
      return new ListView('Reading Now', docsView)

    case BookPageType.finished:
      docsView.enableActionsButton()
      await docsView.setList('finished')
      return new ListView('Finished', docsView)

    case BookPageType.history:
      docsView.enableContinueReading()
      docsView.enableActionsButton()
      await docsView.setList('history')
      await docsView.sort(
        continueReading19038Variant() === 'B'
          ? DocSortOption.viewed
          : DocSortOption.accessed,
      )
      return new ListView('History', docsView)

    case BookPageType.offline:
      await docsView.filterByOffline()
      return new ListView('Offline Books', docsView)

    case BookPageType.downloads:
      await docsView.filterByExports()
      return new ListView('Download History', docsView)
  }
  throw new Error('Unexpected page type: ' + pageType)
}

/**
 * Create a list view for books based on the page type, get data from the books API.
 */
async function createBooksListView(pageType: BookPageType): Promise<ListView> {
  const [docsView, ownedIds] = await Promise.all([
    loadBooks(),
    backend.getOwnedBookIds(),
  ])
  if (
    pageType === BookPageType.new ||
    pageType === BookPageType.popular ||
    pageType === BookPageType.recommended
  ) {
    docsView.excludeByIds(ownedIds)
  }

  switch (pageType) {
    case BookPageType.all:
      return new ListView('All Books', docsView)

    case BookPageType.new:
      await docsView.sort(DocSortOption.first_published_at)
      await docsView.setDocsLimit(20)
      return new ListView('New Releases', docsView)

    case BookPageType.popular:
      await docsView.sort(DocSortOption.popular)
      await docsView.setDocsLimit(20)
      return new ListView('Popular', docsView)

    case BookPageType.free:
      docsView.filterByFree()
      return new ListView('Free', docsView)

    case BookPageType.recommended:
      await docsView.sort(DocSortOption.recommended)
      await docsView.setDocsLimit(20)
      return new ListView('Recommended', docsView)
  }
  throw new Error('Unexpected page type: ' + pageType)
}

async function loadCollections(): Promise<CollectionsView> {
  const collectionsData = await backend.getBookCollections()
  return new CollectionsView(collectionsData)
}

/**
 * Load articles from the backend and create DocsView.
 */
async function loadArticles(
  tag?: string,
  docsUserData?: DocsUserDataResponse,
): Promise<DocsView> {
  const response = await backend.getArticles()
  const view = await ArticlesView.createView(response, docsUserData, true)
  if (tag) {
    await view.setTag(tag)
  }
  return view
}

type ArticleSeries = {
  title: string
  description: string
  articleSlugs: string[]
}

/**
 * Static article series, keyed by the series URL slug.
 */
export const articleSeries: Record<string, ArticleSeries> = {
  'leadership-playbook': {
    title: 'Leadership Playbook',
    description:
      'A practical guide to becoming a better leader, drawing on ideas from books in leadership, management, and psychology',
    // Temporary slugs.
    articleSlugs: [
      'ego-is-the-enemy-of-good-leadership-rasmus-hougaard-jacqueline-carter-harvard-business-review',
      '10-traits-of-a-perfect-boss-according-to-research-by-google-justin-bariso-insider',
      'how-to-design-an-agenda-for-an-effective-meeting-roger-schwarz-harvard-business-review',
      'how-t-mobile-shifted-12000-call-center-employees-to-work-from-home-in-less-than-two-weeks-aaron-pressman-fortune',
      'enjoy-the-extra-day-off-more-bosses-give-4-day-workweek-a-try-yuki-noguchi-national-public-radio',
    ],
  },
}

/**
 * Load article series.
 * Unpublished articles are skipped.
 */
async function loadArticleSeries(articleSeriesSlug: string): Promise<DocsView> {
  const { articleSlugs } = articleSeries[articleSeriesSlug]
  const response = await backend.getArticles()
  const articlesBySlug = new Map(
    response.data.map((article) => [article.url_slug, article]),
  )
  const seriesArticles = articleSlugs
    .map((slug) => articlesBySlug.get(slug))
    .filter((article) => article !== undefined)
  return ArticlesView.createView(
    { ...response, data: seriesArticles },
    undefined,
    true,
    DocSortOption.none,
  )
}

/**
 * Create a list view for articles based on the page type.
 */
async function createArticlesListView(
  pageType: ArticlePageType,
  docsUserDataPromise?: Promise<DocsUserDataResponse>,
): Promise<ListView> {
  const isUserDocs =
    pageType === ArticlePageType.favorites ||
    pageType === ArticlePageType.viewed

  const docsUserData = isUserDocs
    ? await assertNotNull(docsUserDataPromise)
    : undefined

  const docsView = await loadArticles(undefined, docsUserData)

  switch (pageType) {
    case ArticlePageType.all:
      return new ListView('All Articles', docsView)

    case ArticlePageType.favorites:
      await docsView.setIsFavorite(true)
      return new ListView('Favorites', docsView)

    case ArticlePageType.viewed:
      docsView.enableContinueReading()
      docsView.enableActionsButton()
      await docsView.setList('viewed')
      await docsView.sort(DocSortOption.accessed)
      return new ListView('Viewed', docsView)
  }
  throw new Error('Unexpected page type: ' + pageType)
}

/**
 * Create a list view with search results.
 */
export async function createFollowedPodcastEpisodesView(): Promise<ListView> {
  const showPodcastAsSubtitle = true
  const response = await backend.getFollowedPodcastEpisodes()
  const docsView = new PodcastEpisodesView(
    response,
    showPodcastAsSubtitle,
    DocSortOption.none,
  )
  await docsView.setDocsLimit(100)
  return new ListView('Latest Episodes', docsView, {
    viewAllLink: '/app/podcasts/episodes/list/followed',
  })
}

/**
 * Create a list view for podcast episodes based on the page type.
 *
 * This is used for list pages opened from the My Library
 * blocks: favorites, reading now and finished for podcasts tab.
 */
async function createLibraryPodcastEpisodesView(
  pageType: PodcastPageType,
  docsUserDataPromise: Promise<DocsUserDataResponse>,
): Promise<ListView> {
  const [userDocs, docsUserData] = await Promise.all([
    backend.getUserDocs(),
    docsUserDataPromise,
  ])
  const episodes = filteredDocsByDocType(userDocs, 'podcast_episode')
  const title = {
    favorites: 'Favorites',
    reading: 'Reading Now',
    finished: 'Finished',
  }[pageType]

  const episodesView = new PodcastEpisodesView(
    episodes.data,
    false,
    DocSortOption.none,
  )
  episodesView.setUserData(docsUserData)
  await episodesView.init()

  episodesView.enableActionsButton()

  if (pageType === PodcastPageType.reading) {
    episodesView.enableContinueReading()
    await episodesView.setList('reading')
  }

  if (pageType === PodcastPageType.favorites) {
    await episodesView.setIsFavorite(true)
  }

  if (pageType === PodcastPageType.finished) {
    await episodesView.setList('finished')
  }

  return new ListView(title, episodesView)
}

/**
 * Load search results for podcast episodes.
 */
async function searchPodcastEpisodes(searchTerm: string): Promise<DocsView> {
  const response = await searchDocsLimited('podcast_episode', searchTerm)
  return PodcastEpisodesView.createView(
    response,
    undefined,
    false,
    DocSortOption.none,
  )
}

/**
 * Load search results for podcasts.
 */
async function searchPodcasts(searchTerm: string): Promise<DocsListView> {
  return PodcastsView.createSearchView(searchTerm)
}

/**
 * A helper function to load search results for books.
 */
async function searchBooks(searchTerm: string): Promise<DocsView> {
  const response = await searchDocsLimited('book', searchTerm)
  return BooksView.createView(
    response,
    undefined, // No docsUserData
    DocSortOption.none,
    true, // Set showCtaBlock
  )
}

export async function searchArticles(searchTerm: string): Promise<DocsView> {
  const response = await searchDocsLimited('article', searchTerm)
  return ArticlesView.createView(response, undefined, true, DocSortOption.none)
}

/**
 * A helper function to load search results.
 */
async function searchDocsLimited(
  docType: DocType,
  searchTerm: string,
): Promise<ApiResultDocs> {
  // We limit the search results to 100 documents.
  // In the future we need to increase this or/and add paging here.
  const limit = 100
  return await backend.getDocSearch(docType, searchTerm, limit)
}

type RouteParams = {
  name?: string
  category?: string
  term?: string
  url_slug?: string
  collection?: string
  series?: string
}

/**
 * Create a listing view based on the route name and parameters.
 */
export async function createListViewByRoute(
  routeName: string,
  params: RouteParams,
  docsUserDataPromise: Promise<DocsUserDataResponse>,
): Promise<ListView> {
  if (routeName === 'articleCategory') {
    const tag = decodeURI(params.category as string)
    const title = capitalizeTagFirstLetter(tag)
    const docsView = await loadArticles(tag)
    return new ListView(title, docsView)
  }

  if (routeName === 'articleSeries') {
    const articleSeriesSlug = assertNotNull(params.series)
    const docsView = await loadArticleSeries(articleSeriesSlug)
    const { title, description } = articleSeries[articleSeriesSlug]
    return new ListView(title, docsView, { description })
  }

  if (routeName === 'bookCategory') {
    const tag = decodeURI(params.category as string)
    const title = capitalizeTagFirstLetter(tag)
    const docsView = await loadBooks(tag)
    return new ListView(title, docsView)
  }

  if (routeName === 'bookSpecialListingPage') {
    const pageType = params.name as BookPageType
    if (userBookPages.includes(pageType)) {
      // Load data from the home API
      return createUserBooksListView(pageType, docsUserDataPromise)
    } else {
      // Load data from the books API
      return createBooksListView(pageType)
    }
  }

  if (routeName === 'articleSpecialListingPage') {
    const pageType = params.name as ArticlePageType
    return createArticlesListView(pageType, docsUserDataPromise)
  }

  if (routeName === 'bookSearchPage') {
    const term = params.term as string
    const title = `Search results for "${truncateSearchTerm(term)}"`
    const docsView = await searchBooks(term)
    return new ListView(title, docsView)
  }

  if (routeName === 'articleSearchPage') {
    const term = params.term as string
    const title = `Search results for "${truncateSearchTerm(term)}"`
    const docsView = await searchArticles(term)
    return new ListView(title, docsView)
  }

  if (routeName === 'podcast_episodes.search') {
    const term = params.term as string
    const title = `Search results for "${truncateSearchTerm(term)}"`
    const docsView = await searchPodcastEpisodes(term)
    return new ListView(title, docsView)
  }

  if (routeName === 'podcast_episodes.followed') {
    return await createFollowedPodcastEpisodesView()
  }

  if (routeName === 'episodesLibraryListingPage') {
    const pageType = params.name as PodcastPageType
    return await createLibraryPodcastEpisodesView(pageType, docsUserDataPromise)
  }

  if (routeName === 'podcast.search') {
    const term = params.term as string
    const title = `Search results for "${truncateSearchTerm(term)}"`
    const docsView = await searchPodcasts(term)
    return new ListView(title, docsView)
  }

  if (routeName === 'podcastPage') {
    const podcastUrlSlug = params.url_slug as string
    const [podcast, docsUserData] = await Promise.all([
      backend.getPodcast(podcastUrlSlug),
      docsUserDataPromise,
    ])

    const descriptionView = new ListDescriptionViewPodcast(
      podcast,
      podcast.docs.length.toString(),
      docsUserData,
    )
    const docsView = new PodcastEpisodesView(
      podcast.docs,
      false, // Show podcast as subtitle
      DocSortOption.none,
    )
    docsView.setUserData(docsUserData)
    docsView.enableContinueReading()
    await docsView.init()
    const view = new ListView(podcast.title, docsView, {
      descriptionView,
      filterTitle: 'Episodes',
    })
    return view
  }

  if (routeName === 'bookCollection') {
    const urlSlug = params.collection as string
    const { docsView, collection } = await loadCollectionBooks(urlSlug)
    const descriptionView = new ListDescriptionView(
      collection,
      collection.doc_count,
    )
    return new ListView(descriptionView.title, docsView, { descriptionView })
  }

  if (routeName === 'collections') {
    const title = 'All Collections'
    const docsView = await loadCollections()
    return new ListView(title, docsView)
  }

  if (routeName === 'customList') {
    const url_slug = assertNotNull(params.url_slug)

    const { docsView, list } = await loadCustomListDocs(
      url_slug,
      docsUserDataPromise,
    )

    await docsView.setCustomList(list.id)
    docsView.enableActionsButton()

    return new ListView(list.title, docsView, { customList: list })
  }

  throw new Error('Unexpected route name: ' + routeName)
}

/**
 * Create a listing view based on the route name and parameters.
 *
 * This takes different arguments than createListViewByRoute() because
 * the docs and user data have already been loaded by the MyLibrary page.
 */
export async function createListViewByRoute20321(
  routeName: string,
  params: RouteParams,
  docs: Doc[],
  docsUserData: DocsUserDataResponse,
  lists: CustomListWithDocs[],
): Promise<ListView> {
  if (routeName === 'my_library.favorites') {
    const docsView = await createDocsView(
      docs,
      docsUserData,
      DocSortOption.viewed,
    )
    docsView.enableActionsButton()
    await docsView.setIsFavorite(true)
    return new ListView('Favorites', docsView, {
      customCardComponent: 'AnyDocCard',
    })
  }
  if (routeName === 'my_library.finished') {
    const docsView = await createDocsView(
      docs,
      docsUserData,
      DocSortOption.viewed,
    )
    docsView.enableActionsButton()
    await docsView.setList('finished')
    return new ListView('Finished', docsView, {
      customCardComponent: 'AnyDocCard',
    })
  }
  if (routeName === 'my_library.reading') {
    const docsView = await createDocsView(
      docs,
      docsUserData,
      DocSortOption.viewed,
    )
    docsView.enableContinueReading()
    docsView.enableActionsButton()
    await docsView.setList('reading')
    await docsView.setOnlyInProgress(false)
    return new ListView('Reading Now', docsView, {
      customCardComponent: 'AnyDocCard',
    })
  }
  if (routeName === 'my_library.offline') {
    const docsView = await createDocsView(
      docs,
      docsUserData,
      DocSortOption.viewed,
    )
    docsView.enableActionsButton()
    await docsView.filterByOffline()
    return new ListView('Offline Books', docsView, {
      customCardComponent: 'AnyDocCard',
    })
  }
  if (routeName === 'my_library.downloads') {
    const docsView = await createDocsView(
      docs,
      docsUserData,
      DocSortOption.viewed,
    )
    docsView.enableActionsButton()
    await docsView.filterByExports()
    return new ListView('Download History', docsView, {
      customCardComponent: 'AnyDocCard',
    })
  }
  if (routeName === 'my_library.customList') {
    const index = lists.findIndex((list) => list.url_slug === params.url_slug)
    if (index < 0) {
      throw new Error('Unexpected list url_slug: ' + params.url_slug)
    }
    const list = lists[index]
    const docsView = await createDocsView(
      docs,
      docsUserData,
      DocSortOption.viewed,
    )
    docsView.enableActionsButton()
    await docsView.setCustomList(list.id)
    return new ListView(list.title, docsView, {
      customCardComponent: 'AnyDocCard',
      customList: list,
    })
  }

  throw new Error('Unexpected route name: ' + routeName)
}

/**
 * Old My Library tabs, to be rerouted in Save-for-Later.
 */
const OLD_LIBRARY_TABS_20321 = new Set([
  'my_library.articles',
  'my_library.episodes',
  'my_library.custom_lists',
])

/**
 * Old listing pages and their new Library list routes in Save-for-Later.
 */
const OLD_LISTING_PAGES_20321: Record<string, Record<string, string>> = {
  bookSpecialListingPage: {
    [BookPageType.favorites]: 'my_library.favorites',
    [BookPageType.reading]: 'my_library.reading',
    [BookPageType.finished]: 'my_library.finished',
    [BookPageType.offline]: 'my_library.offline',
    [BookPageType.downloads]: 'my_library.downloads',
  },
  articleSpecialListingPage: {
    [ArticlePageType.favorites]: 'my_library.favorites',
    [ArticlePageType.viewed]: 'my_library.books',
  },
  episodesLibraryListingPage: {
    [PodcastPageType.favorites]: 'my_library.favorites',
    [PodcastPageType.reading]: 'my_library.reading',
    [PodcastPageType.finished]: 'my_library.finished',
  },
}

/**
 * Returns the new Library route for an old Library/list URL (passing the query
 * through as is), or null if we leave the route alone.
 */
export function saveForLater20321Redirect(
  to: Pick<RouteLocationNormalized, 'name' | 'params' | 'query'>,
): RouteLocationRaw | null {
  if (!saveForLater20321Treatment()) {
    return null
  }
  const routeName = String(to.name)
  const query = { ...to.query }

  if (OLD_LIBRARY_TABS_20321.has(routeName)) {
    return { name: 'my_library.books', query }
  }
  if (routeName === 'customList') {
    return {
      name: 'my_library.customList',
      params: { url_slug: to.params.url_slug },
      query,
    }
  }
  const listName = OLD_LISTING_PAGES_20321[routeName]?.[String(to.params.name)]
  if (listName) {
    return { name: listName, query }
  }
  return null
}
